import * as React from "react";
import { $, merge } from "glamor";
import InputMask from "react-input-mask";
import { DATATEST } from "constants/integration";
import moment from "moment";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IProps {
    [key: string]: any;
    editable?: boolean;
    maskFormat: string;
}

interface IState {
    value: string;
    editMode: EDIT_MODES;
}

enum EDIT_MODES {
    REPLACE,
    APPEND,
}

export class DateCellEditor extends React.PureComponent<IProps, IState> {
    private inputRef;
    private takeDefaultValue = false;
    private defaultValue = 0;
    private styles = merge(
        $(" input", {
            width: "100%",
            height: "27px",
        })
    );
    constructor(props) {
        super(props);
        let editMode = EDIT_MODES.APPEND;
        let value = props.value;
        this.defaultValue = value;
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            value = undefined;
        } else if (props.charPress) {
            value = props.charPress;
            editMode = EDIT_MODES.REPLACE;
        } else {
            value = this.formatTimestamp(value);
        }
        this.state = {
            value,
            editMode,
        };
    }

    componentDidMount() {
        this.focus();
    }

    // Check if cell should select all when starts editing
    selectAllOnStart() {
        const { charPress, eventKey } = this.props;
        if (eventKey === KEYBOARD_KEYS.ENTER) {
            // On ENTER press
            return true;
        } else if (!eventKey && !charPress) {
            // On mouse double click or focused on tab
            return true;
        }

        return false;
    }

    focus() {
        if (this.inputRef) {
            (this.inputRef as any).getInputDOMNode().focus();
        }
    }

    getValue() {
        const value = this.takeDefaultValue ? this.defaultValue : this.state.value;
        const date = moment.utc(value, this.props.maskFormat);

        if (date.isValid()) {
            return date.unix();
        } else {
            return 0;
        }
    }

    handleChange = (event) => {
        const newValue = event.target.value;
        this.setState({ value: newValue }, () => {
            if (!newValue) {
                this.inputRef.setCursorPosition(0);
            }
        });
    };

    formatTimestamp = (timestamp: number) => {
        if (!timestamp) {
            return "";
        }

        return moment.unix(timestamp).utc().format(this.props.maskFormat);
    };

    setRef = (ref) => {
        this.inputRef = ref;
    };

    render() {
        return (
            <div {...this.styles} onKeyDown={this.handleKeyDown}>
                <InputMask
                    data-test={DATATEST.ASSORTMENTS.GRID.DATE_CELL_EDITOR}
                    mask={this.props.maskFormat.replace(/[a-zA-Z]/g, "9")}
                    ref={this.setRef}
                    value={this.state.value}
                    onChange={this.handleChange}
                    alwaysShowMask
                    maskplaceholder={this.props.maskFormat}
                />
            </div>
        );
    }

    private handleKeyDown = (e: React.KeyboardEvent) => {
        const { editMode } = this.state;
        const { api } = this.props;
        const currentCell = api.getEditingCells()[0];

        switch (e.key) {
            case KEYBOARD_KEYS.LEFT:
            case KEYBOARD_KEYS.RIGHT:
                // Suggestions will cause defaultPrevented to be true if using key events
                if (!e.defaultPrevented && editMode === EDIT_MODES.REPLACE) {
                    this.props.stopEditing();
                    this.navigateToCell(e, currentCell);
                    e.preventDefault();
                }
                break;
            case KEYBOARD_KEYS.ENTER:
                this.props.stopEditing();
                this.navigateToCell(e, currentCell);
                break;
            case KEYBOARD_KEYS.ESCAPE:
                this.takeDefaultValue = true;
                this.props.stopEditing();
                break;
            default:
        }
    };

    private navigateToCell = (e: React.KeyboardEvent, currentCell) => {
        const { api } = this.props;
        if (currentCell) {
            api.setFocusedCell(currentCell.rowIndex, currentCell.column.getColId());
        }

        const lastRow = api.getLastDisplayedRow();

        const focusedCell = api.getFocusedCell();
        api.clearRangeSelection();
        switch (e.key) {
            case KEYBOARD_KEYS.RIGHT:
                api.tabToNextCell();
                break;
            case KEYBOARD_KEYS.LEFT:
                api.tabToPreviousCell();
                break;
            case KEYBOARD_KEYS.UP:
                if (focusedCell.rowIndex > 0) {
                    api.setFocusedCell(Math.max(0, focusedCell.rowIndex - 1), focusedCell.column.getColId());
                }
                break;
            case KEYBOARD_KEYS.DOWN:
            case KEYBOARD_KEYS.ENTER:
                api.setFocusedCell(Math.min(focusedCell.rowIndex + 1, lastRow), focusedCell.column.getColId(), null);
                break;
            default:
        }
    };
}
